import { BrandKitFormFields } from './BrandKitForm.types'
import { ColorPicker } from '@dtx-company/design-system/src/components/ColorPicker/ColorPicker'
import { FC } from 'react'
import { FlowcodeInput } from './subcomponents/FlowcodeInput'
import { Grid } from '@dtx-company/design-system/src/components/Grid/Grid'
import { HEX_CODE_REGEX } from '@dtx-company/design-system/src/components/ColorPicker/util'
import { LogoUpload } from './subcomponents/LogoUpload'
import { SkeletonField } from './subcomponents/SkeletonField'
import { TextField } from '@dtx-company/design-system/src/components/TextField/TextField'
import { useBrandKitForm } from './BrandKitForm.hooks'

/**
 * Renders the Brand Kit form used on the templates page.
 * @important Must be wrapped in both a {@link BrandKitFormProvider} and a {@link DesignSystemThemeProvider}.
 */
export const BrandKitForm: FC = () => {
  const {
    onCTAChanged,
    onPrimaryColorChanged,
    onSecondaryColorChanged,
    onLogoChanged,
    formMethods,
    onSubmit,
    initialBrandKit
  } = useBrandKitForm()

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = formMethods

  const { ref: primaryTextRef, ...primaryTextProps } = register(BrandKitFormFields.PrimaryText)
  const {
    ref: primaryColorRef,
    onChange: _onPrimaryColorChange,
    ...primaryColorProps
  } = register(BrandKitFormFields.PrimaryColor, {
    pattern: { value: HEX_CODE_REGEX, message: 'Invalid hex code' }
  })
  const {
    ref: secondaryColorRef,
    onChange: _onSecondaryColorChange,
    ...secondaryColorProps
  } = register(BrandKitFormFields.SecondaryColor, {
    pattern: { value: HEX_CODE_REGEX, message: 'Invalid hex code' }
  })

  return (
    <Grid
      container
      columns={24}
      direction="row"
      spacing={{ xs: 4, lg: 2 }}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      data-testid="brand-kit-form"
    >
      <Grid xs={24} md={12} lg={6}>
        <FlowcodeInput />
      </Grid>

      <Grid xs={24} md={12} lg={5} xl={7}>
        <SkeletonField>
          <TextField
            inputRef={primaryTextRef}
            {...primaryTextProps}
            label="Your scan prompt"
            placeholder="Scan to review"
            onBlur={onCTAChanged}
            size="large"
            fullWidth
            error={!!errors[BrandKitFormFields.PrimaryText]}
            helperText={errors[BrandKitFormFields.PrimaryText]?.message}
          />
        </SkeletonField>
      </Grid>

      <Grid xs={24} md={12} lg={4}>
        <LogoUpload onLogoChanged={onLogoChanged} />
      </Grid>

      <Grid xs={12} md={6} lg={4.5} xl={3.5}>
        <SkeletonField>
          <ColorPicker
            inputRef={primaryColorRef}
            onChange={onPrimaryColorChanged}
            {...primaryColorProps}
            label="Primary color"
            size="large"
            fullWidth
            error={!!errors[BrandKitFormFields.PrimaryColor]}
            helperText={errors[BrandKitFormFields.PrimaryColor]?.message}
            defaultValue={initialBrandKit?.primaryColor}
            allowEmpty
            placeholder="hex code"
          />
        </SkeletonField>
      </Grid>

      <Grid xs={12} md={6} lg={4.5} xl={3.5}>
        <SkeletonField>
          <ColorPicker
            inputRef={secondaryColorRef}
            onChange={onSecondaryColorChanged}
            {...secondaryColorProps}
            label="Secondary color"
            size="large"
            fullWidth
            error={!!errors[BrandKitFormFields.SecondaryColor]}
            helperText={errors[BrandKitFormFields.SecondaryColor]?.message}
            defaultValue={initialBrandKit?.secondaryColor}
            allowEmpty
            placeholder="hex code"
          />
        </SkeletonField>
      </Grid>
    </Grid>
  )
}
